import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { v4 as uuid4 } from 'uuid';
import { AssetListSharedState, AssetListStateService } from './asset-list-state.service';
import { AssetListService } from '../../../services/asset-list.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AssetModelComponent } from '../../asset-model/asset-model.component';
import { DateHelperService } from '../../../services/date-helper.service';
import { EmitterService } from '../../../services/emitter.service';
import { Asset, Project, SubAsset } from '../models/asset.model';

@Injectable({
  providedIn: 'root',
})
export class AssetListVariantService {
  constructor(
    private readonly stateService: AssetListStateService,
    private readonly modalService: NgbModal,
    private readonly translatePipe: TranslatePipe,
    private readonly dateHelperService: DateHelperService,
    private readonly assetListService: AssetListService
  ) {}

  public changeProjectVarientStatus(
    content: Asset,
    variantId: string,
    status: string,
    project?: Project,
    projectId?: string
  ): void {
    const state: AssetListSharedState = this.stateService.currentState;
    const affectedAssets: Record<string, Asset & { deliveryDate: string | null }> = { ...state.affectedAssets };

    if (!affectedAssets[content.id].subAsset) {
      affectedAssets[content.id] = { ...affectedAssets[content.id], subAsset: [] };
    }

    const asset: Asset & { deliveryDate: string | null } = affectedAssets[content.id];
    if (asset?.subAsset && Array.isArray(asset.subAsset)) {
      const variant: SubAsset = asset.subAsset.find((item: SubAsset) => item.id === variantId);
      if (variant) {
        variant.status = status;
      } else {
        asset.subAsset.push({ id: variantId, status: status });
      }
    }

    if (asset) {
      affectedAssets[content.id] = asset;
      this.stateService.updateState({ affectedAssets });
      this.updateStatus(asset, asset.status, project, projectId);
    }
  }

  public getVariantStatus(asset: Asset, id: string): string | null {
    if (!asset?.subAsset?.length) return asset?.status || null;

    if (!id) return asset.status;

    for (let i: number = 0; i < asset.subAsset?.length; ++i) {
      if (asset.subAsset[i].id == id) return asset.subAsset[i].status;
    }
    return asset.status;
  }

  public updateStatus(
    asset: Asset & { deliveryDate: string | null },
    status: string,
    project?: any,
    projectId?: string
  ): void {
    asset.status = status;

    const parsedDate: Date | null = asset.deliveryDate
      ? this.dateHelperService.fromDatePickerToDate(asset.deliveryDate)
      : null;
    const deliveryDate: string | null =
      parsedDate && this.dateHelperService.isValidDate(parsedDate)
        ? this.dateHelperService.fromDateToUTCString(parsedDate)
        : null;

    this.assetListService.updateAssetListStatusOrDelivery({ ...asset, deliveryDate }).subscribe({
      next: (data: any) => {
        if (project && !project?.assetList) {
          project.assetList = [];
        }

        if (project) {
          project.assetList = project.assetList.map((a: any) => (a?.assetListId === data?.assetListId ? data : a));
        }

        if (project && projectId) {
          EmitterService.get('PROJECT_ASSET_UPDATED').emit({
            projectId: projectId,
            assetList: project.assetList,
          });
        }
      },
    });
  }

  public deleteVariant(
    content: Asset,
    variant: SubAsset,
    bundle: Asset[],
    originalBundle: Asset[],
    deleteCallback?: (data: { id: string; subAssetId: string }) => void,
    bundleChangeCallback?: (bundle: Asset[]) => void
  ): Promise<{ newBundle: Asset[]; newOriginalBundle: Asset[] } | null> {
    const modalRef: NgbModalRef = this.modalService.open(AssetModelComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = this.translatePipe.transform('variant_deleteVariant') || 'Delete Variant';
    modalRef.componentInstance.description = `Are you sure you want to delete the variant <strong>"${variant.detail}"</strong>?`;

    return modalRef.result
      .then((confirm: boolean) => {
        if (!confirm) return null;
        if (!originalBundle?.length) return null;

        const state: AssetListSharedState = this.stateService.currentState;

        const newOriginalBundle: Asset[] = originalBundle.map((a: Asset) => {
          if (a.id === content.id) {
            return {
              ...a,
              subAsset: a.subAsset.filter((v: SubAsset) => v.id !== variant.id),
            };
          }
          return a;
        });

        const allBundle: Asset[] = state.allBundle?.map((a: Asset) => {
          if (a.id === content.id) {
            return {
              ...a,
              subAsset: a.subAsset.filter((v: SubAsset) => v.id !== variant.id),
            };
          }
          return a;
        });

        const newBundle: Asset[] = bundle?.map((a: Asset) => {
          if (a.id === content.id) {
            return {
              ...a,
              subAsset: a.subAsset.filter((v: SubAsset) => v.id !== variant.id),
            };
          }
          return a;
        });

        this.stateService.updateState({
          allBundle,
          allAssets: structuredClone(newOriginalBundle),
        });

        if (deleteCallback) {
          deleteCallback({
            id: content.id,
            subAssetId: variant.id,
          });
        }

        const affectedAssets: Record<string, Asset & { deliveryDate: string | null }> = { ...state.affectedAssets };
        const asset: Asset & { deliveryDate: string | null } = affectedAssets[content.id];
        if (asset?.subAsset && Array.isArray(asset.subAsset)) {
          const index: number = asset.subAsset.findIndex((item: SubAsset) => item.id === variant.id);
          if (index !== -1) {
            asset.subAsset.splice(index, 1);
          }
        }
        if (asset) {
          affectedAssets[content.id] = asset;
          this.stateService.updateState({ affectedAssets });
          this.updateStatus(asset, asset.status);
        }

        if (bundleChangeCallback) {
          bundleChangeCallback(newBundle);
        }

        return { newBundle, newOriginalBundle };
      })
      .catch(() => null);
  }

  public duplicateVariant(
    content: Asset,
    variant: SubAsset,
    bundle: Asset[],
    originalBundle: Asset[],
    projectId?: string,
    asWidget?: boolean,
    assetUpdatedCallback?: (content: Asset) => void,
    bundleChangeCallback?: (bundle: Asset[]) => void,
    timelineUpdateCallback?: (data: { allBundle: Asset[]; updatedAsset: Asset }) => void
  ): { newBundle: Asset[]; newOriginalBundle: Asset[] } {
    const state: AssetListSharedState = this.stateService.currentState;
    const duplicatedVariant: SubAsset = structuredClone(variant);

    duplicatedVariant.id = uuid4();

    const originalTitle: string = variant.title || variant.detail || '';
    const baseTitle: string = originalTitle.replace(/\s*\(\d+\)\s*$/, '');

    let highestCopyNumber: number = 0;
    if (content.subAsset && content.subAsset.length > 0) {
      const regex: RegExp = new RegExp(String.raw`^${baseTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\s*\((\d+)\)$`);

      for (const v of content.subAsset) {
        const subAssetOriginalTitle: string = v.title || v.detail || '';
        const match: RegExpMatchArray | null = subAssetOriginalTitle.match(regex);
        if (match?.[1]) {
          const copyNumber: number = Number.parseInt(match[1], 10);
          if (copyNumber > highestCopyNumber) {
            highestCopyNumber = copyNumber;
          }
        }
      }
    }

    duplicatedVariant.title = `${baseTitle} (${highestCopyNumber + 1})`;
    duplicatedVariant.detail = `${baseTitle} (${highestCopyNumber + 1})`;

    if (!content.subAsset) {
      content.subAsset = [];
    }

    content.subAsset.push(duplicatedVariant);

    const newOriginalBundle: Asset[] = originalBundle.map((a: Asset) => {
      if (a.id === content.id) {
        return { ...content };
      }
      return a;
    });

    const allBundle: Asset[] = state.allBundle?.map((a: Asset) => {
      if (a.id === content.id) {
        return { ...content };
      }
      return a;
    });

    const newBundle: Asset[] = bundle?.map((a: Asset) => {
      if (a.id === content.id) {
        return { ...content };
      }
      return a;
    });

    this.stateService.updateState({
      allBundle,
      allAssets: structuredClone(newOriginalBundle),
    });

    if (assetUpdatedCallback) {
      assetUpdatedCallback(content);
    }

    if (bundleChangeCallback) {
      bundleChangeCallback(newBundle);
    }

    if (projectId && asWidget && timelineUpdateCallback) {
      timelineUpdateCallback({
        allBundle: state.allBundle,
        updatedAsset: content,
      });
    }

    return { newBundle, newOriginalBundle };
  }
}
